/**
 * Приход — один лист для настройки и для «Деньги → Доход» (22.10.2026).
 *
 * Прежде это были две формы об одном: в настройке был «Ждёшь конкретный
 * приход?», в приложении — счёт, «Источник действует» и «Это аванс к
 * зарплате». Подпись одного и того же поля разошлась: «Какого числа приходит»
 * против «Какие числа». Теперь лист один и устроен как лист кредита: сумма
 * крупно, остальное строками, выбор — в своём листе, галочки — переключатели
 * с пояснением (макет docs/design/forms-2026-10-22).
 *
 * Чего у места нет, лист не показывает: в настройке счета вводят шагом позже,
 * и строки «На какой счёт» там нет — приход ложится на первую карту, как и
 * прежде.
 */
import { useState, type ReactNode } from 'react';
import { ZERO, type Account, type IncomeRhythm, type PlainDate, type ShiftRule } from '@skoka/core';
import { SheetWrap } from './sheet.js';
import {
  BigAmount, CardAccount, CardDate, CardDays, CardHead, CardInput, CardMoney, CardPick, CardToggle, MoreButton,
} from './card-fields.js';
import { parseAmountInput, todayPd } from './store.js';
import { dayMonth } from './text.js';

export type IncomeRhythmKind = 'dates' | 'weekly' | 'biweekly' | 'irregular' | 'none';

/**
 * Приход в черновике — то, что правит лист.
 *
 * `rhythm` появился потому, что «какого числа» — не описание жизни. У
 * сдельщика числа нет вовсе, у половины фриланса деньги приходят раз в
 * неделю, а спрашивать число у того, кому его назвать нечем, значит получить
 * выдуманный ответ и считать от него всю картину.
 */
export interface IncomeDraft {
  name: string;
  amount: string;
  /**
   * Первое из чисел прихода. Держится ради старых черновиков настройки и
   * равно первому из `days`: прежде оно оставалось 10-м (у «Аванса» — 25-м),
   * что бы человек ни выбрал, и шло в расчёт уже прошедших платежей.
   */
  day: string;
  variable: boolean;
  rhythm: IncomeRhythmKind;
  /** Для «по числам»: одно или несколько, через запятую. */
  days: string;
  /** Для недельного и двухнедельного: ближайший приход. */
  anchor: string;
  /** Для «когда как»: ждёт ли человек конкретных денег. Обе строки необязательны. */
  expectDate: string;
  expectAmount: string;
  /** Должны ли текущие деньги дожить до этого прихода. */
  expectIsCheckpoint: boolean;
  /** Задаёт ли этот источник срок, до которого растягивают деньги. */
  isCheckpoint: boolean;
  /** Аванс к зарплате: срок задаёт, период «Аналитики» не режет. */
  advance?: boolean;
  /** Куда приходят деньги — в приложении. В настройке — первая карта. */
  accountId?: string;
  /** Источник действует — при правке в приложении. */
  active?: boolean;
}

export const newIncome = (name = '', day = '10'): IncomeDraft => ({
  name, amount: '', day, variable: false,
  rhythm: 'dates', days: day, anchor: '', expectDate: '', expectAmount: '',
  expectIsCheckpoint: false, isCheckpoint: true,
});

const isDate = (v: string | undefined): boolean => /^\d{4}-\d{2}-\d{2}$/.test(v ?? '');
const dayNum = (sv: string): number => Math.min(31, Math.max(1, Math.trunc(Number(sv) || 1)));

/** Числа месяца из строки «10, 25»: только 1–31, по порядку, без повторов. */
export const parseDays = (s: string | undefined): number[] =>
  [...new Set((s ?? '').split(/[^\d]+/).map(Number).filter((n) => Number.isInteger(n) && n >= 1 && n <= 31))]
    .sort((a, b) => a - b);

/**
 * Ритм из черновика.
 *
 * Непонятное число молча не исправляется: если список пуст, остаётся прежний
 * день, названный человеком.
 */
export function draftRhythm(inc: IncomeDraft, shiftRule: ShiftRule = 'previous_business_day'): IncomeRhythm {
  if (inc.rhythm === 'irregular') return { kind: 'irregular' };
  if (inc.rhythm === 'none') return { kind: 'none' };
  if (inc.rhythm === 'weekly' || inc.rhythm === 'biweekly') {
    const anchor = isDate(inc.anchor) ? (inc.anchor as PlainDate) : todayPd();
    return inc.rhythm === 'weekly' ? { kind: 'weekly', anchor } : { kind: 'biweekly', anchor };
  }
  const days = parseDays(inc.days);
  return { kind: 'dates', days: days.length > 0 ? days : [dayNum(inc.day)], shiftRule };
}

/** Пять ритмов — с пояснением под каждым: прежде оно пряталось в «i». */
export const RHYTHM_OPTIONS: Array<{ id: IncomeRhythmKind; title: string; sub: string }> = [
  { id: 'dates', title: 'По числам', sub: 'зарплата, пенсия: 10-го или 10-го и 25-го' },
  { id: 'weekly', title: 'Каждую неделю', sub: 'считаем от ближайшего прихода' },
  { id: 'biweekly', title: 'Раз в две недели', sub: 'считаем от ближайшего прихода' },
  { id: 'irregular', title: 'Когда как', sub: 'фриланс, подработки: дат нет — посчитаем, на сколько хватит текущих денег' },
  { id: 'none', title: 'Пока не жду', sub: 'считаем только то, что уже на счетах' },
];

export function IncomeSheet({ init, isNew, title, accounts, editing = false, preview, onSave, onDelete, onClose }: {
  init: IncomeDraft;
  isNew: boolean;
  /** Заголовок; по умолчанию «Новый приход» / название прихода. */
  title?: string;
  /** Счета для «На какой счёт». Нет — строки нет (настройка). */
  accounts?: Account[];
  /** Правка сохранённого источника: «Источник действует». */
  editing?: boolean;
  /** Что станет со сроком — считает тот, у кого есть вся картина. */
  preview?: (v: IncomeDraft) => ReactNode;
  onSave: (v: IncomeDraft) => void | Promise<void>;
  onDelete?: () => void | Promise<void>;
  onClose: () => void;
}) {
  const today = todayPd();
  const [v, setV] = useState<IncomeDraft>(init);
  const [busy, setBusy] = useState(false);
  const on = <K extends keyof IncomeDraft>(k: K, x: IncomeDraft[K]) => setV((p) => ({ ...p, [k]: x }));
  const dated = v.rhythm === 'dates';
  const weekly = v.rhythm === 'weekly' || v.rhythm === 'biweekly';
  const days = parseDays(v.days);
  // «Ещё» открыт сразу, если там что-то уже включено: иначе правка прятала
  // бы выбор, сделанный самим человеком.
  const [more, setMore] = useState(Boolean(init.advance) || init.active === false);
  const hasMore = dated || editing;

  // «Когда как» и «пока не жду» не требуют суммы: человек её не знает, и
  // требовать число значит получить выдуманное.
  const ready = !busy
    && (v.rhythm === 'irregular' || v.rhythm === 'none' || parseAmountInput(v.amount) > ZERO)
    && (!dated || days.length > 0)
    && (!weekly || isDate(v.anchor))
    && (!accounts || Boolean(v.accountId));

  const save = async () => {
    setBusy(true);
    try {
      await onSave({
        ...v,
        name: v.name.trim() || 'Приход',
        // Одно число вместо двух: первое из выбранных.
        ...(dated && days.length > 0 ? { day: String(days[0]), days: days.join(', ') } : {}),
        // Аванс бывает только у прихода по числам.
        ...(dated ? {} : { advance: false }),
      });
    } finally { setBusy(false); }
  };

  return (
    <SheetWrap title={title ?? (isNew ? 'Новый приход' : v.name || 'Приход')} onClose={onClose} className="rcpt">
      <div className="rcform">
        {v.rhythm !== 'none' && (
          <BigAmount label={v.rhythm === 'irregular' ? 'Сколько обычно приходит' : 'Сколько приходит'}
            {...(v.rhythm === 'irregular' ? { hint: 'можно не знать' } : {})}
            value={v.amount} onChange={(x) => on('amount', x)} />
        )}

        <div className="rccard rcsel">
          <CardInput label="Название" value={v.name} onChange={(x) => on('name', x)} placeholder="Зарплата" width={170} />
          <CardPick label="Как приходит" value={v.rhythm} onChange={(x) => on('rhythm', x as IncomeRhythmKind)}
            options={RHYTHM_OPTIONS} {...(dated ? { sub: 'каждый месяц' } : {})} />
          {dated && (
            <CardDays label="Какого числа" value={days} max={4}
              note="Можно несколько чисел — например, аванс и зарплату. 31-е в коротком месяце — последний день."
              onChange={(ds) => setV((p) => ({ ...p, days: ds.join(', '), day: String(ds[0] ?? p.day) }))} />
          )}
          {weekly && (
            <CardDate label="Ближайший приход" value={v.anchor} min={today}
              text={isDate(v.anchor) ? dayMonth(v.anchor, today) : 'Укажи дату'}
              sub="не раньше сегодня" onChange={(d) => on('anchor', d)} />
          )}
          {accounts && (
            <CardAccount label="На какой счёт" pickerTitle="Зачислять на счёт" accounts={accounts}
              value={v.accountId ?? ''} onChange={(id) => on('accountId', id)} />
          )}
        </div>
        {weekly && !isDate(v.anchor) && (
          <div className="warnline">Без ближайшего прихода не от чего считать — укажи дату.</div>
        )}
        {v.rhythm === 'none' && (
          <p className="note">Посчитаем только то, что уже на счетах. Сумму и даты добавишь, когда станут известны.</p>
        )}

        {/*
          Конкретный ожидаемый гонорар — отдельная запись, а не расписание: он
          случается один раз, и обучать им ритм нельзя. Роль прихода, а не его
          размер: обещанный возврат за куртку не может быть точкой планирования,
          а гонорар сдельщика — может, и решает это человек.
        */}
        {v.rhythm === 'irregular' && (
          <>
            <CardHead>Ждёшь конкретный приход?</CardHead>
            <div className="rccard rcsel">
              <CardDate label="Когда" value={v.expectDate} min={today} clear="Не жду"
                text={isDate(v.expectDate) ? dayMonth(v.expectDate, today) : 'не жду'}
                onChange={(d) => on('expectDate', d)} />
              <CardMoney label="Сколько ждёшь" value={v.expectAmount} onChange={(x) => on('expectAmount', x)}
                placeholder="можно не знать" />
              {isDate(v.expectDate) && (
                <CardToggle label="До этих денег должны дожить текущие" checked={v.expectIsCheckpoint}
                  sub={v.expectIsCheckpoint
                    ? `срок «Можно тратить» — до ${dayMonth(v.expectDate, today)}`
                    : 'срок не изменится: деньги просто пополнят бюджет'}
                  onChange={(x) => on('expectIsCheckpoint', x)} />
              )}
            </div>
          </>
        )}

        {v.rhythm !== 'none' && (
          <div className="rccard rcsel">
            <CardToggle label="Сумма плавает" sub="в расчёте — нижняя граница, 90%"
              checked={v.variable} onChange={(x) => on('variable', x)} />
            {/*
              Роль источника, а не его размер. Подработка 25-го приходит раньше
              зарплаты 5-го, но жить до неё никто не собирается — если считать её
              точкой, окно схлопнется, а «Доступно» вырастет на пустом месте.
            */}
            {(dated || weekly) && (
              <CardToggle label="До этих денег живём" checked={v.isCheckpoint}
                sub={v.isCheckpoint ? 'с него считается «Можно тратить до …»' : 'срок не сокращает — просто пополнит деньги'}
                onChange={(x) => on('isCheckpoint', x)} />
            )}
          </div>
        )}

        {hasMore && <MoreButton label="Ещё" open={more} onToggle={() => setMore(!more)} />}
        {hasMore && more && (
          <div className="rccard rcsel">
            {/*
              Аванс — признак, а не название: «Первая часть зарплаты» остаётся
              авансом. Срок главной он задаёт, а период «Аналитики» не режет —
              иначе аренда попала бы в одну из половин месяца.
            */}
            {dated && (
              <CardToggle label="Это аванс к зарплате" sub="«Аналитика» считает период от зарплаты до зарплаты"
                checked={Boolean(v.advance)} onChange={(x) => on('advance', x)} />
            )}
            {editing && (
              <CardToggle label="Источник действует" sub="выключи, если этих денег больше не будет"
                checked={v.active !== false} onChange={(x) => on('active', x)} />
            )}
          </div>
        )}

        {preview?.(v)}

        <button className="btn pri" disabled={!ready} onClick={() => void save()}>{isNew ? 'Добавить' : 'Сохранить'}</button>
        {onDelete && !isNew && (
          <button className="textact danger" disabled={busy}
            onClick={() => { if (confirm('Удалить источник дохода?')) void onDelete(); }}>Удалить</button>
        )}
      </div>
    </SheetWrap>
  );
}
